import React from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Card, FormGroup } from "@cofano/cck";
import { Box, Typography } from "@mui/material";

import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission, Language, usePermission } from "utils";
import { LocationLink, RelationLink, VoyageLink } from "utils/LinkUtils";

import { locationDescriptionWithGroup } from "view/locations/utils/LocationUtils";
import { CargoFromQuery } from "view/cargos/CargoDetails";
import { RouteInfo } from "view/cargos/components/createEdit/Actions";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import ConnectingVisitField from "view/cargos/components/connectingVisits/ConnectingVisitField";
import CargoActionTypeTag from "view/cargos/components/tags/CargoActionTypeTag";

const CargoActionsDetails = ({ cargo }: { cargo: CargoFromQuery }) => {

	const session = useStoreState(state => state.session);
	const showLocationGroup = hasPluginPermission(session, "showLocationGroup");

	const { isRTR, isSkipperOrShipManager, isCustomer } = usePermission();

	return (
		<Box
			p={2}
			sx={{ backgroundColor: theme => theme.palette.mode === "dark" ? "inherit" : "#F0F0F0" }}
			className="cargo-form-actions">
			<div className="cargo-actions">
				{cargo.actions
					.sort((a, b) => (a.start ?? 0) - (b.start ?? 0))
					.map((action, index) => {
						const currentLocation = action.location;
						const nextLocation = cargo.actions[index + 1]?.location;
						const location = cargo.actions[index].location;

						return (
							<div className="cargo-action" key={action.id}>
								<Card
									title={(
										<span>
											{showLocationGroup && location ? locationDescriptionWithGroup(location) : location?.description}
										</span>
									)}>
									<React.Fragment>
										<FormGroup labelPlacement={"left-center"} label={Language("TYPE")} overflow="hidden">
											<CargoActionTypeTag type={action.type} />
										</FormGroup>

										<FormGroup label={intl.formatMessage({ id: "SURVEYOR" })} labelPlacement="left-center" overflow="hidden">
											<RelationLink relation={action.surveyor} />
										</FormGroup>

										<FormGroup labelPlacement={"left-center"} label={Language("LOCATION")} overflow="hidden">
											<LocationLink location={action.location} />
										</FormGroup>

										<FormGroup label={intl.formatMessage({ id: "REFERENCE" })} labelPlacement="left-center" overflow="hidden">
											<Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "small" }}>
												{action.reference}
											</Typography>
										</FormGroup>

										{action.connectingShipPortVisit?.ship && (
											<FormGroup labelPlacement={"left-center"} label={Language("CONNECTING_SHIP")} overflow="hidden">
												<div style={{ alignItems: "center", display: "flex" }}>
													{action.connectingShipPortVisit?.ship && (
														<ConnectingVisitField action={action} />
													)}
												</div>
											</FormGroup>
										)}

										{hasPluginPermission(session, "useContainers") ? (
											<>
												<FormGroup labelPlacement={"left-center"} label={Language("CARGO_CONTAINER_TYPE")} overflow="hidden">
													{cargo.containerType?.isoTypeCode}
												</FormGroup>

												<FormGroup labelPlacement={"left-center"} label={Language("TEU")} overflow="hidden">
													{cargo.teu}
												</FormGroup>
											</>
										) : (
											<>
												<FormGroup
													labelPlacement={"left-center"}
													label={Language("WEIGHT")}
													overflow="hidden">
													{DataFormat.tonnage(action.weight)}
												</FormGroup>

												{hasOptionalFieldPermission(session, Fields.ORDER, "volume") && (
													<FormGroup
														labelPlacement={"left-center"}
														label={Language("CARGO_VOLUME")}
														overflow="hidden">
														{DataFormat.volumeM3(action.volume)}
													</FormGroup>
												)}
											</>
										)}

										<FormGroup
											labelPlacement={"left-center"}
											label={Language(action.displayAsWindow ? "START_END_WINDOW" : "DATE")}
											overflow="hidden">
											<CargoActionDate action={action} />
										</FormGroup>

										{action.call && (
											<React.Fragment>
												<h3>{Language("CALL")}</h3>

												<FormGroup labelPlacement={"left-center"} label={Language("STATUS")} overflow="hidden">
													{DataFormat.status(action.call.lastStatusChange?.status || undefined, session.user?.language || "")}
												</FormGroup>

												<FormGroup labelPlacement={"left-center"} label={Language("VOYAGE")} overflow="hidden">
													<VoyageLink voyage={action.call.voyage} />
												</FormGroup>

												<FormGroup labelPlacement={"left-center"} label={Language("ARRIVAL_TIME")} overflow="hidden">
													{DataFormat.callArrivalTime(action.call)}
												</FormGroup>

												<FormGroup labelPlacement={"left-center"} label={Language("DEPARTURE_TIME")} overflow="hidden">
													{DataFormat.callDepartureTime(action.call)}
												</FormGroup>
											</React.Fragment>
										)}

										<FormGroup label={intl.formatMessage({ id: "CARGO_ACTION_NOTES" })} labelPlacement="left-center" overflow="hidden">
											{action.notes || ""}
										</FormGroup>

										{hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "terminalNotes") && (
											<FormGroup label={intl.formatMessage({ id: "TERMINAL_PLANNING_NOTES" })} labelPlacement="left-center" overflow="hidden">
												{action.terminalPlanningNotes || ""}
											</FormGroup>
										)}

									</React.Fragment>
								</Card>

								{(isRTR || isSkipperOrShipManager || isCustomer) && currentLocation && nextLocation && (currentLocation.id !== nextLocation.id) && (
									<RouteInfo
										currentLocationId={currentLocation.id}
										nextLocationId={nextLocation.id} />
								)}
							</div>
						);
					})}
			</div>
		</Box>
	);
};

export default CargoActionsDetails;
